import React, { useEffect, useState } from "react";
import './Material.css';
import { getMaterialUrl } from '../../config/api';
import { MaterialMaxPdfData } from '../../utils/materialsPdfUtils';

interface MaterialMax {
    index: number;
    date: string;
    // PE fields
    plast02651?: number | null;
    HD03490NP?: number | null;
    HD03594PE?: number | null;
    HM0349PE?: number | null;
    HM0359PE?: number | null;
    PE6PP32?: number | null;
    p2nt119?: number | null;
    HD12500LB?: number | null;
    stavrolen27383?: number | null;
    HD07580SB?: number | null;
    plast27673?: number | null;
    HD03580SB?: number | null;
    HD10530LB?: number | null;
    PE100RC?: number | null;
    // PPR fields
    PPI013GP5?: number | null;
    PPG200224?: number | null;
    PPH250GP2?: number | null;
    plast1030?: number | null;
    PPG103508?: number | null;
    PPH030GPTomskneftehim?: number | null;
    PPH030GPNeftehimia?: number | null;
    PPH030GPPolium?: number | null;
    PPH030GPZapSibNeftehim?: number | null;
    SIBEX_PP_R003_EX1?: number | null;
    // PVH fields
    C70Y?: number | null;
    PC271?: number | null;
    SI70?: number | null;
    C5868PG?: number | null;
    C6359M?: number | null;
    C6669PG?: number | null;
    RC267?: number | null;
    C6669GC?: number | null;
    SI67?: number | null;
    C7059M?: number | null;
}

interface MaterialMaxProps {
    materialType: string;
    onPdfDataChange?: (data: MaterialMaxPdfData) => void;
}

function MaterialMax({ materialType, onPdfDataChange }: MaterialMaxProps) {
    const [materialData, setMaterialData] = useState<MaterialMax | null>(null);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchMaterialData = async () => {
            if (!materialType) {
                setError('Material type is required');
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                console.log(`Fetching max data for material type: ${materialType}`);
                const access = localStorage.getItem('access');
                const response = await fetch(getMaterialUrl(materialType, 'max/'), {
                    headers: access ? { 'Authorization': `Bearer ${access}` } : {}
                });
                console.log('Response status:', response.status);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data: MaterialMax[] = await response.json();
                console.log('Received max data:', data);


                const maxData = data.reduce((maxValues, currentItem) => {
                    for (const key of Object.keys(currentItem)) {
                        if (key !== 'index' && key !== 'date') {
                            const currentVal = currentItem[key as keyof MaterialMax];


                            const numericValue = typeof currentVal === 'string' ?
                                parseFloat(currentVal.replace(/\s+/g, '')) :
                                Number(currentVal);

                            if (!isNaN(numericValue)) {
                                maxValues[key] = Math.max(maxValues[key] ?? -Infinity, numericValue);
                            }
                        }
                    }
                    return maxValues;
                }, {} as Record<string, number>);

                setMaterialData({
                    ...data[0],
                    ...maxData,
                    date: data[0].date,
                });

                setError('');
            } catch (error) {
                console.error('Error fetching material max data:', error);
                setError(error instanceof Error ? error.message : 'Failed to fetch data');
            } finally {
                setIsLoading(false);
            }
        };

        fetchMaterialData();
    }, [materialType]);

    const formatPrice = (value: any) => {
        if (value === null || value === undefined) return 'Нет данных';
        const numValue = Number(value);
        if (isNaN(numValue)) return 'Нет данных';
        return `${numValue.toLocaleString('ru-RU')} ₽`;
    };

    const getPrices = (materialData: MaterialMax) => {
        if (materialType === 'ppr') {
            return [
                { label: "PPI 013 GP5", value: materialData.PPI013GP5 },
                { label: "PPG 200224", value: materialData.PPG200224 },
                { label: "PPH 250 GP2", value: materialData.PPH250GP2 },
                { label: "0,30/10", value: materialData.plast1030 },
                { label: "PPG 103508", value: materialData.PPG103508 },
                { label: "PPH 030 GP ТНХ", value: materialData.PPH030GPTomskneftehim },
                { label: "PPH 030 GP Нефтехимия", value: materialData.PPH030GPNeftehimia },
                { label: "PPH 030 GP Полиум", value: materialData.PPH030GPPolium },
                { label: "PPH 030 GP ЗСНХ", value: materialData.PPH030GPZapSibNeftehim },
                { label: "SIBEX PP R003 EX1", value: materialData.SIBEX_PP_R003_EX1 }
            ];
        } else if (materialType === 'pvh') {
            return [
                { label: "C70Y", value: materialData.C70Y },
                { label: "PC271", value: materialData.PC271 },
                { label: "SI70", value: materialData.SI70 },
                { label: "C5868PG", value: materialData.C5868PG },
                { label: "C6359M", value: materialData.C6359M },
                { label: "C6669PG", value: materialData.C6669PG },
                { label: "RC267", value: materialData.RC267 },
                { label: "C6669GC", value: materialData.C6669GC },
                { label: "SI67", value: materialData.SI67 },
                { label: "C7059M", value: materialData.C7059M }
            ];
        } else {
            return [
                { label: "0,26/51", value: materialData.plast02651 },
                { label: "HD 03490 NP", value: materialData.HD03490NP },
                { label: "HD 03594 PE", value: materialData.HD03594PE },
                { label: "HM 0349 PE", value: materialData.HM0349PE },
                { label: "HM 0359 PE", value: materialData.HM0359PE },
                { label: "PE6PP32", value: materialData.PE6PP32 },
                { label: "ПЭ2НТ11-9", value: materialData.p2nt119 },
                { label: "HD 12500 LB", value: materialData.HD12500LB },
                { label: "273-83 Ставролен", value: materialData.stavrolen27383 },
                { label: "HD 07580 SB", value: materialData.HD07580SB },
                { label: "276-73", value: materialData.plast27673 },
                { label: "HD 03580 SB", value: materialData.HD03580SB },
                { label: "HD 10530 LB", value: materialData.HD10530LB },
                { label: "PE 100RC", value: materialData.PE100RC }
            ];
        }
    };

    useEffect(() => {
        if (!onPdfDataChange || !materialData) return;
        onPdfDataChange({
            materialType,
            tiles: getPrices(materialData).map(price => ({ label: price.label, value: formatPrice(price.value) })),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [materialData, materialType, onPdfDataChange]);

    if (isLoading) {
        return <div className="loading-message">Загрузка...</div>;
    }

    if (error) {
        return <div className="error-message">Ошибка: {error}</div>;
    }

    if (!materialData) {
        return <div className="no-data">Нет данных</div>;
    }

    const prices = getPrices(materialData);

    return (
        <div className="material-max-container">
            <h2 className="material-max-title">Максимальная цена</h2>
            <div className={`material-max-prices-container ${materialType}`}>
                {prices.map((price, index) => (
                    <div key={index} className="material-max-price-item">
                        <div className="material-max-price-label">{price.label}</div>
                        <div className="material-max-price-value">{formatPrice(price.value)}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default MaterialMax;
